import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Spin, Drawer, Form, Input, Row, Col, Select, Button, Divider, Alert, Icon } from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import { DrawerProps } from 'antd/lib/drawer';
import { ButtonProps } from 'antd/lib/button';

import { create, details, update, UsersState, getUserCreateState, getUserDetailsState, getUserUpdateState } from '../../store/actions/users';
import { MainReducerState } from '../../store/reducers';
import { User, Roles } from '../../store/api/apiTypes';
import { getErrorMessageFromState } from '../../utils';

interface UserDrawerProps extends FormComponentProps {
    creates: UsersState['create'];
    createUser: typeof create.trigger;
    getDetails: typeof details.trigger;
    id?: User['id'];
    isVisible: boolean;
    onClose: () => void;
    onUpdateSuccess?: () => void;
    userState: UsersState['details'];
    updateUser: typeof update.trigger;
    updates: UsersState['update'];
}

const UserDrawer: FC<UserDrawerProps> = ({
    creates, createUser, form, getDetails, id, isVisible, onClose,
    onUpdateSuccess, userState, updateUser, updates,
}) => {

    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        if (creates.success || updates.success) {
            form.resetFields();
            if (typeof onUpdateSuccess === 'function') {
                onUpdateSuccess();
            }
            onClose();
        }
    }, [creates.success, updates.success]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isVisible) {
            if (id !== undefined) {
                getDetails({ id });
            }
        }
        setIsEditing(!!id);
    }, [isVisible]); // eslint-disable-line react-hooks/exhaustive-deps

    const onDrawerClose: DrawerProps['onClose'] & ButtonProps['onClick'] = () => {
        onClose();
        form.resetFields();
    };

    const onRoleChange = (role: User['role']) => {
        form.setFieldsValue({ role });
    };

    const onSubmit = (e?: React.FormEvent) => {
        if (e) {
            e.preventDefault();
        }
        form.validateFieldsAndScroll(async (err, val) => {
            if (err) {
                return;
            }

            if (id !== undefined) {
                updateUser({ id, data: val });
            } else {
                createUser({ data: val});
            }
        });
    };

    const { getFieldDecorator } = form;

    const requiredCharsNumber = form.getFieldValue('role') === 'consultation' ? 8 : 12;
    const regexp1: RegExp = new RegExp(`^[0-9a-zA-Z!@#$%^&*]{${requiredCharsNumber}}`);
    const regexp2: RegExp = new RegExp('^(?=.*[a-zA-Z])(?=.*[0-9])');
    const regexp3: RegExp = new RegExp('^(?=.*[A-Z])');
    const regexp4: RegExp = new RegExp('(?=.*[!@#$%^&*])');

    const passwordCondition1: boolean = form.getFieldValue('password') && regexp1.test(form.getFieldValue('password'));
    const passwordCondition2: boolean = form.getFieldValue('password') && regexp2.test(form.getFieldValue('password'));
    const passwordCondition3: boolean = form.getFieldValue('password') && regexp3.test(form.getFieldValue('password'));
    const passwordCondition4: boolean = form.getFieldValue('password') && regexp4.test(form.getFieldValue('password'));

    const createErrorMessage = getErrorMessageFromState(creates);
    const updateErrorMessage = getErrorMessageFromState(updates);

    return (
        <>
            <Drawer
                title={id !== undefined ? `Détail de l'utilisateur` : `Creation d'utilisateur`}
                width={960}
                onClose={onDrawerClose}
                visible={isVisible}
            >
                <Spin spinning={userState.loading} tip="Chargement...">
                    {(!isEditing || (isEditing && !userState.loading && userState.data)) && (
                        <Form onSubmit={onSubmit} layout="vertical">
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="Prénom">
                                        {getFieldDecorator('firstName', {
                                            rules: [{
                                                required: true,
                                                message: 'champ requis',
                                            }],
                                            initialValue: isEditing && userState.data ?
                                                userState.data.firstName : '',
                                        })(
                                            <Input placeholder="prénom"/>,
                                        )}
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Nom">
                                        {getFieldDecorator('lastName', {
                                            rules: [{
                                                required: true,
                                                message: 'champ requis',
                                            }],
                                            initialValue: isEditing && userState.data ?
                                                userState.data.lastName : '',
                                        })(
                                            <Input placeholder="nom"/>,
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="Email">
                                        {getFieldDecorator('email', {
                                            rules: [{
                                                required: true,
                                                message: 'champ requis',
                                            }],
                                            initialValue: isEditing && userState.data ?
                                                userState.data.email : '',
                                        })(
                                            <Input placeholder="adresse email"/>,
                                        )}
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Téléphone">
                                        {getFieldDecorator('phone', {
                                            rules: [{
                                                required: false,
                                                message: 'champ requis',
                                            }],
                                            initialValue: isEditing && userState.data ?
                                                userState.data.phone : '',
                                        })(
                                            <Input placeholder="Votre téléphone" />,
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item label="Fonction">
                                        {getFieldDecorator('position', {
                                            rules: [{
                                                required: false,
                                                message: 'champ requis',
                                            }],
                                            initialValue: isEditing && userState.data ?
                                                userState.data.position : '',
                                        })(
                                            <Input placeholder="Votre poste" />,
                                        )}
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Droits">
                                        {getFieldDecorator('role', {
                                            rules: [{
                                                required: true,
                                                message: 'champ requis',
                                            }],
                                            initialValue: isEditing && userState.data && userState.data.role !== null ?
                                            userState.data.role : undefined,
                                        })((
                                            <Select
                                                filterOption={false}
                                                onChange={onRoleChange}
                                                placeholder="Droits"
                                            >
                                                <Select.Option value={Roles.admin}>
                                                    Administrateur
                                                </Select.Option>
                                                <Select.Option value={Roles.manager}>
                                                    Gestionnaire
                                                </Select.Option>
                                                <Select.Option value={Roles.user}>
                                                    Consultation
                                                </Select.Option>
                                                <Select.Option value={Roles.signup}>
                                                    Nouvel Inscrit (aucun droit)
                                                </Select.Option>
                                            </Select>
                                        ))}
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    { id === undefined && form.getFieldValue('role') !== undefined && (
                                        <Form.Item label="Mot de passe">
                                            {getFieldDecorator('password', {
                                                rules: [{
                                                    required: false,
                                                    max: 12,
                                                    message: 'champ requis',
                                                },
                                                /*{
                                                    pattern: new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})'),
                                                    message: 'Format de mot de passe incorrect',
                                                }*/],
                                                initialValue: '',
                                            })(
                                                <Input.Password placeholder="mot de passe"/>,
                                            )}
                                        </Form.Item>
                                    )}
                                </Col>
                            </Row>
                            <hr/>
                            { id === undefined && form.getFieldValue('role') !== undefined && false && (
                                <>
                                    <div className={'check-password ' + ((passwordCondition1) ? 'active' : '')}>
                                        <Icon
                                            type={(passwordCondition1) ? 'check' : 'close'}
                                            style={(passwordCondition1) ? { color: 'green' } : { color: 'red' }}
                                        />
                                        &nbsp; Au moins {requiredCharsNumber} caractères
                                    </div>
                                    <div className={'check-password ' + ((passwordCondition2) ? 'active' : '')}>
                                        <Icon
                                            type={(passwordCondition1) ? 'check' : 'close'}
                                            style={(passwordCondition1) ? { color: 'green' } : { color: 'red' }}
                                        />
                                        &nbsp; Au moins une lettre et un chiffre
                                    </div>
                                    <div className={'check-password ' + ((passwordCondition3) ? 'active' : '')}>
                                        <Icon
                                            type={(passwordCondition1) ? 'check' : 'close'}
                                            style={(passwordCondition1) ? { color: 'green' } : { color: 'red' }}
                                        />
                                        &nbsp; Au moins une lettre en majuscule
                                    </div>
                                    <div className={'check-password ' + ((passwordCondition4) ? 'active' : '')}>
                                        <Icon
                                            type={(passwordCondition1) ? 'check' : 'close'}
                                            style={(passwordCondition1) ? { color: 'green' } : { color: 'red' }}
                                        />
                                        &nbsp; Au moins un caractère spécial
                                    </div>
                                    <hr/>
                                </>
                            )}
                            {!creates.loading && creates.error && (
                                <Form.Item>
                                    <Alert
                                        message={'Une erreur est survenue: ' + createErrorMessage}
                                        type="error"
                                    />
                                </Form.Item>
                            )}
                            {!updates.loading && updates.error && (
                                <Form.Item>
                                    <Alert
                                        message={'Une erreur est survenue: ' + updateErrorMessage}
                                        type="error"
                                    />
                                </Form.Item>
                            )}
                            <div className="form-actions">
                                <Button
                                    htmlType="submit"
                                    type="primary"
                                    onClick={onSubmit}
                                    loading={updates.loading || creates.loading}
                                >
                                    {id !== undefined ? 'Mettre à jour' : 'Ajouter'}
                                </Button>
                                <Divider type="vertical"/>
                                <Button onClick={onDrawerClose} type="ghost">
                                    Annuler
                                </Button>
                            </div>
                        </Form>
                    )}
                </Spin>
            </Drawer>
        </>
    );
};

const mapStateToProps = (state: MainReducerState) => ({
    creates: getUserCreateState(state),
    userState: getUserDetailsState(state),
    updates: getUserUpdateState(state),
});

const UserFormDrawer = Form.create<UserDrawerProps>()(UserDrawer);

export default connect(
    mapStateToProps,
    {
        createUser: create.trigger,
        getDetails: details.trigger,
        updateUser: update.trigger,
    },
)(UserFormDrawer);
